// src/utils/frameMeasurements.js

// Segments shorter than this (in cm) are treated as absent and skipped entirely,
// e.g. the profile on a frameless Print, or a mat set to zero.
const MIN_SEGMENT = 0.001;

export const WIDTH_LABEL_POSITIONS = ["below", "above"];
export const HEIGHT_LABEL_POSITIONS = ["left", "right"];
export const ORIENTATIONS = ["vertical", "horizontal", "both"];

/** Accept only a known choice; anything else falls back to the default. */
export function normalizeChoice(value, allowed, fallback) {
  return allowed.includes(value) ? value : fallback;
}

/**
 * Bundle a run of boundary points with the measured gaps between them.
 * `startIndex` maps a segment back onto the point that begins it, so the
 * renderer can find a segment's endpoints after projection.
 */
function makeAxis(id, points, values) {
  return {
    id,
    points,
    segments: values
      .map((value, index) => ({ value, startIndex: index }))
      .filter((segment) => segment.value > MIN_SEGMENT),
  };
}

/**
 * Resolve the profile width and depth for a frame type.
 * Mirrors the switch that builds `boundingMesh` in ControlContext so the gizmo
 * can never disagree with the geometry that is actually on screen.
 */
function resolveProfile(config) {
  switch (config.frameType) {
    case "Box":
      return { width: config.boxProfileWidth, depth: config.boxProfileDepth };
    case "Float":
      return {
        width: config.floatProfileWidth,
        depth: config.floatProfileDepth,
      };
    case "Aluminum":
      return {
        width: config.aluminumProfileWidth,
        depth: config.aluminumProfileDepth,
      };
    default:
      // Print, Face Mount and Canvas add no profile.
      return { width: 0, depth: 0.1 };
  }
}

/**
 * Compute the measurement stacks for the current frame, in world units (which
 * are centimeters, 1:1).
 *
 * Returns `axes` — one entry per requested orientation, each holding the
 * boundary points in world space and the measured gaps between them. The
 * vertical axis runs top to bottom (profile, mat, print, mat, profile); the
 * horizontal axis runs left to right over the same stack.
 */
export function computeFrameMeasurements(config) {
  const profile = resolveProfile(config);
  const {
    printWidth,
    printHeight,
    borderWidth,
    borderHeight,
    borderOffsetX,
    borderOffsetY,
    measurementsPosition,
    measurementsOrientation,
    totalWidthLabelPosition,
    totalHeightLabelPosition,
  } = config;

  const inputs = [
    printWidth,
    printHeight,
    borderWidth,
    borderHeight,
    borderOffsetX,
    borderOffsetY,
    profile.width,
    profile.depth,
  ];
  if (inputs.some((value) => !Number.isFinite(value))) {
    return { valid: false, axes: [], corners: [] };
  }

  const profileWidth = Math.max(0, profile.width);
  const halfW = printWidth / 2 + borderWidth + profileWidth;
  const halfH = printHeight / 2 + borderHeight + profileWidth;

  // Read the mat gaps off the rendered geometry rather than borderTop/borderLeft.
  // These are algebraically identical, but stay correct even if the derivation
  // effect in ControlContext has not re-run yet after a border change.
  const matTop = borderHeight - borderOffsetY;
  const matBottom = borderHeight + borderOffsetY;
  const matLeft = borderWidth + borderOffsetX;
  const matRight = borderWidth - borderOffsetX;

  const pct = Number.isFinite(measurementsPosition)
    ? Math.min(100, Math.max(0, measurementsPosition))
    : 25;
  // The same percentage drives both axes: for the vertical line it runs across
  // the width (0% = left edge), for the horizontal line down the height
  // (0% = top edge).
  const worldX = -halfW + (pct / 100) * (2 * halfW);
  const worldY = halfH - (pct / 100) * (2 * halfH);

  // Each axis is a run of boundary points plus the gaps between them. Building
  // both the same way keeps the drawing code orientation-agnostic.
  const verticalAxis = makeAxis(
    "vertical",
    [
      halfH,
      halfH - profileWidth,
      printHeight / 2 + borderOffsetY,
      -printHeight / 2 + borderOffsetY,
      -halfH + profileWidth,
      -halfH,
    ].map((y) => ({ x: worldX, y })),
    [profileWidth, matTop, printHeight, matBottom, profileWidth],
  );

  const horizontalAxis = makeAxis(
    "horizontal",
    [
      -halfW,
      -halfW + profileWidth,
      borderOffsetX - printWidth / 2,
      borderOffsetX + printWidth / 2,
      halfW - profileWidth,
      halfW,
    ].map((x) => ({ x, y: worldY })),
    [profileWidth, matLeft, printWidth, matRight, profileWidth],
  );

  const orientation = normalizeChoice(
    measurementsOrientation,
    ORIENTATIONS,
    "vertical",
  );
  const axes = [];
  if (orientation !== "horizontal") axes.push(verticalAxis);
  if (orientation !== "vertical") axes.push(horizontalAxis);

  return {
    valid: axes.some((axis) => axis.segments.length > 0),
    orientation,
    // Sit the lines on the frontmost plane so they do not sink into the
    // moulding when the camera is off-axis.
    worldZ: profile.depth,
    halfW,
    halfH,
    axes,
    totalWidth: 2 * halfW,
    totalHeight: 2 * halfH,
    // Outer corners, clockwise from top-left. The total labels hang off the
    // midpoints of these edges.
    corners: [
      { x: -halfW, y: halfH },
      { x: halfW, y: halfH },
      { x: halfW, y: -halfH },
      { x: -halfW, y: -halfH },
    ],
    widthLabelPosition: normalizeChoice(
      totalWidthLabelPosition,
      WIDTH_LABEL_POSITIONS,
      "below",
    ),
    heightLabelPosition: normalizeChoice(
      totalHeightLabelPosition,
      HEIGHT_LABEL_POSITIONS,
      "left",
    ),
  };
}

/** Format a centimeter value for display. Never emits NaN/Infinity. */
export function formatCm(value) {
  if (!Number.isFinite(value)) return "";
  const fixed = value.toFixed(1);
  return `${fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed} cm`;
}
